import React from 'react';
import { CaretDown, WarningCircle } from '@phosphor-icons/react';
import { typInfo } from '../../../lib/zaehler/typen';
import { useImmoTheme } from '../../../lib/immobilien/theme';

/** Typ-Icon in farbiger Box (Kachel 40/13/19, Tabelle 36/12/17). */
export function TypIcon({ typ, groesse = 40 }: { typ: string; groesse?: 36 | 40 }) {
  const t = typInfo(typ);
  const IconKomp = t.icon;
  return (
    <span className="flex items-center justify-center shrink-0" aria-hidden
      style={{ width: groesse, height: groesse, borderRadius: groesse === 40 ? 13 : 12, background: t.flaeche, color: t.farbe }}>
      <IconKomp weight="fill" size={groesse === 40 ? 19 : 17} />
    </span>
  );
}

/** Typ-Chip in der Typfarbe (Kachel 22 px, Tabelle 24 px). */
export function TypChip({ typ, groesse = 22 }: { typ: string; groesse?: 22 | 24 }) {
  const t = typInfo(typ);
  return (
    <span className="inline-flex items-center whitespace-nowrap shrink-0"
      style={{ height: groesse, padding: '0 9px', borderRadius: groesse / 2, fontSize: groesse === 22 ? 10 : 11, fontWeight: 800, background: t.flaeche, color: t.farbe }}>
      {t.label}
    </span>
  );
}

/** Natives Select im Feldstil (Filterleiste 46 px / Formular 50 px wie TextFeld, --bg). */
export function FeldSelect({ wert, onChange, ariaLabel, id, hoehe = 46, children, style }: {
  wert: string; onChange: (v: string) => void; ariaLabel?: string; id?: string; hoehe?: 46 | 50; children: React.ReactNode; style?: React.CSSProperties;
}) {
  const [theme] = useImmoTheme();
  return (
    <span className="relative flex min-w-0" style={style}>
      <select id={id} value={wert} onChange={e => onChange(e.target.value)} aria-label={ariaLabel} className="w-full min-w-0 cursor-pointer"
        style={{
          height: hoehe, padding: '0 38px 0 14px', borderRadius: hoehe === 50 ? 15 : 14, border: 'none', outline: 'none', appearance: 'none', WebkitAppearance: 'none',
          background: 'var(--bg)', boxShadow: 'inset 0 0 0 1px var(--line)', color: 'var(--ink)', fontSize: 14, fontWeight: 700,
          fontFamily: 'inherit', colorScheme: theme,
        }}>
        {children}
      </select>
      <CaretDown size={13} weight="bold" color="var(--ink3)" className="absolute pointer-events-none" style={{ right: 14, top: '50%', transform: 'translateY(-50%)' }} />
    </span>
  );
}

/** Label 12/700 --ink3 über einem Feld. */
export function FeldBlock({ label, htmlFor, children, voll }: { label: string; htmlFor?: string; children: React.ReactNode; voll?: boolean }) {
  return (
    <div className="flex flex-col min-w-0" style={{ gap: 6, gridColumn: voll ? '1 / -1' : undefined }}>
      <label htmlFor={htmlFor} style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>{label}</label>
      {children}
    </div>
  );
}

/** Mehrzeiliges Notizfeld (2 Zeilen, nicht vergrößerbar). */
export function NotizFeld({ id, wert, onChange }: { id: string; wert: string; onChange: (v: string) => void }) {
  return (
    <textarea id={id} value={wert} onChange={e => onChange(e.target.value)} rows={2}
      style={{ resize: 'none', padding: '12px 14px', borderRadius: 15, border: 'none', outline: 'none', background: 'var(--bg)', boxShadow: 'inset 0 0 0 1px var(--line)', color: 'var(--ink)', fontSize: 14, fontWeight: 600, fontFamily: 'inherit' }} />
  );
}

/** Fehlerbanner oben im Formular (Handoff Zähler §5.4). */
export function FehlerBanner({ text }: { text: string }) {
  return (
    <div role="alert" className="flex items-start" style={{ gap: 9, padding: '11px 14px', borderRadius: 14, background: 'var(--bad-soft)', color: 'var(--bad)', fontSize: 13, fontWeight: 700 }}>
      <WarningCircle weight="fill" size={17} style={{ flexShrink: 0, marginTop: 1 }} />{text}
    </div>
  );
}
